import React from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Phone, Mail, ArrowUpRight } from 'lucide-react';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const companyLinks = [
    { name: 'About Us', path: '/about' },
    { name: 'Our Companies', path: '/companies' },
    { name: 'Services', path: '/services' },
    { name: 'Contact', path: '/contact' },
  ];

  const serviceLinks = [
    { name: 'Packaging Manufacturing', path: '/services#packaging' },
    { name: 'Cleaning Services', path: '/services#cleaning' },
    { name: 'Laundry Services', path: '/services#laundry' },
  ];

  return (
    <footer data-testid="main-footer" className="bg-slate-900 text-white">
      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-24">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand Column */}
          <div className="lg:col-span-1">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-xl">A</span>
              </div>
              <div>
                <span className="font-bold text-xl tracking-tight">AUMKAR</span>
                <span className="text-xs block text-slate-400 -mt-1">Group of Companies</span>
              </div>
            </div>
            <p className="text-slate-400 text-sm leading-relaxed mb-6">
              A global group delivering excellence in packaging manufacturing and essential services across India and Oman.
            </p>
          </div>

          {/* Company Links */}
          <div>
            <h4 className="font-semibold text-lg mb-6">Company</h4>
            <ul className="space-y-4">
              {companyLinks.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    data-testid={`footer-link-${link.name.toLowerCase().replace(/\s/g, '-')}`}
                    className="text-slate-400 hover:text-white transition-colors text-sm flex items-center gap-1 group"
                  >
                    {link.name}
                    <ArrowUpRight className="w-3 h-3 opacity-0 -translate-y-1 translate-x-1 group-hover:opacity-100 group-hover:translate-y-0 group-hover:translate-x-0 transition-all" />
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Services Links */}
          <div>
            <h4 className="font-semibold text-lg mb-6">Services</h4>
            <ul className="space-y-4">
              {serviceLinks.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    data-testid={`footer-link-${link.name.toLowerCase().replace(/\s/g, '-')}`}
                    className="text-slate-400 hover:text-white transition-colors text-sm flex items-center gap-1 group"
                  >
                    {link.name}
                    <ArrowUpRight className="w-3 h-3 opacity-0 -translate-y-1 translate-x-1 group-hover:opacity-100 group-hover:translate-y-0 group-hover:translate-x-0 transition-all" />
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="font-semibold text-lg mb-6">Contact</h4>
            <ul className="space-y-4">
              <li>
                <div className="flex items-start gap-3">
                  <MapPin className="w-4 h-4 text-blue-500 mt-1 flex-shrink-0" />
                  <div>
                    <p className="text-sm text-white font-medium">India Office</p>
                    <p className="text-slate-400 text-sm">Dundigal, Hyderabad, India</p>
                  </div>
                </div>
              </li>
              <li>
                <div className="flex items-start gap-3">
                  <MapPin className="w-4 h-4 text-blue-500 mt-1 flex-shrink-0" />
                  <div>
                    <p className="text-sm text-white font-medium">Oman Office</p>
                    <p className="text-slate-400 text-sm">Muscat, Oman</p>
                  </div>
                </div>
              </li>
              <li>
                <a
                  href="tel:+919036240229"
                  data-testid="footer-phone"
                  className="flex items-center gap-3 text-slate-400 hover:text-white transition-colors"
                >
                  <Phone className="w-4 h-4 text-blue-500" />
                  <span className="text-sm">+91 90362 40229</span>
                </a>
              </li>
              <li>
                <a
                  href="mailto:aumkarprivatelimited@gmail.com"
                  data-testid="footer-email"
                  className="flex items-center gap-3 text-slate-400 hover:text-white transition-colors"
                >
                  <Mail className="w-4 h-4 text-blue-500" />
                  <span className="text-sm">aumkarprivatelimited@gmail.com</span>
                </a>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-slate-800">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-slate-500 text-sm">
              © {currentYear} AUMKAR Group of Companies. All rights reserved.
            </p>
            <div className="flex items-center gap-6">
              <Link to="/privacy" className="text-slate-500 hover:text-slate-300 text-sm transition-colors">
                Privacy Policy
              </Link>
              <Link to="/terms" className="text-slate-500 hover:text-slate-300 text-sm transition-colors">
                Terms of Service
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
